'use es6';

import { useState, useCallback, useMemo } from 'react';
import { useMutation } from 'data-fetching-client';
import { useSelector } from 'react-redux';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HelpfulButton from 'marketplace-ui-reviews/components/HelpfulButton';
import ReviewActionsWrapper from 'marketplace-ui-reviews/components/ReviewActionsWrapper';
import ReportButton from 'marketplace-ui-reviews/components/ReportButton';
import ReportReviewModal from 'marketplace-ui-reviews/components/modals/ReportReviewModal';
import { Reportables } from 'marketplace-ui-reviews/constants/ReportReasons';
import { SET_REVIEW_HELPFUL } from 'marketplace-ui-reviews/queries/setReviewHelpfulQuery';
import { REPORT_REVIEW } from 'marketplace-ui-reviews/queries/reportReviewQuery';
import { REPORT_REPLY } from 'marketplace-ui-reviews/queries/reportReplyQuery';
import { getUserMarkedHelpful } from 'marketplace-ui-reviews/utils/getUserMarkedHelpful';
import { getIsCustomer, getUserId } from '../../../selectors/auth';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const withReportModalState = WrappedComponent => props => {
  const {
    review
  } = props;
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const isCustomer = useSelector(getIsCustomer);
  const userId = useSelector(getUserId);
  const userMarkedHelpful = getUserMarkedHelpful(userId, review);
  const [localMarkedHelpful, setLocalMarkedHelpful] = useState(userMarkedHelpful);
  const onReviewModalOpen = () => setReviewModalOpen(true);
  const onReplyModalOpen = () => setReplyModalOpen(true);
  const onReviewModalClose = () => setReviewModalOpen(false);
  const onReplyModalClose = () => setReplyModalOpen(false);
  const [setIsHelpful] = useMutation(SET_REVIEW_HELPFUL);
  const [reportReview] = useMutation(REPORT_REVIEW);
  const [reportReply] = useMutation(REPORT_REPLY);
  const buildReportModal = useCallback((type, onConfirm, onClose) => /*#__PURE__*/_jsx(ReportReviewModal, {
    review: review,
    type: type,
    onClose: onClose,
    onConfirm: onConfirm
  }), [review]);
  const onReviewModalConfirm = ({
    reason,
    comment
  }) => {
    return new Promise((resolve, reject) => {
      reportReview({
        variables: {
          reviewId: review.get('id'),
          reportReason: reason,
          comment
        }
      }).then(() => {
        onReviewModalClose();
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  };
  const onReplyModalConfirm = ({
    reason,
    comment
  }) => {
    return new Promise((resolve, reject) => {
      reportReply({
        variables: {
          reviewId: review.get('id'),
          replyId: review.getIn(['reply', 'id']),
          reportReason: reason,
          comment
        }
      }).then(() => {
        onReplyModalClose();
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  };
  const reportReviewModal = buildReportModal(Reportables.REVIEW, onReviewModalConfirm, onReviewModalClose);
  const reportReplyModal = buildReportModal(Reportables.REPLY, onReplyModalConfirm, onReplyModalClose);
  const handleClickHelpful = useCallback(() => {
    return new Promise((resolve, reject) => {
      setIsHelpful({
        variables: {
          reviewId: review.get('id'),
          isHelpful: !localMarkedHelpful
        }
      }).then(() => {
        setLocalMarkedHelpful(!localMarkedHelpful);
        resolve('Successfully marked helpful');
      }).catch(err => {
        Raven.captureMessage('Failed to mark review as helpful', {
          level: 'error',
          extra: Object.assign({}, err)
        });
        reject(err);
      });
    });
  }, [review, localMarkedHelpful, setIsHelpful]);
  const helpfulCount = useMemo(() => {
    const currentHelpfulCount = review.getHelpfulCount();
    if (userMarkedHelpful && !localMarkedHelpful) {
      return currentHelpfulCount > 0 ? currentHelpfulCount - 1 : 0;
    }
    if (!userMarkedHelpful && localMarkedHelpful) {
      return currentHelpfulCount + 1;
    }
    return currentHelpfulCount;
  }, [localMarkedHelpful, review, userMarkedHelpful]);
  const reviewsActionsList = useMemo(() => /*#__PURE__*/_jsxs(ReviewActionsWrapper, {
    className: "vr-hidden",
    children: [/*#__PURE__*/_jsx(HelpfulButton, {
      className: "m-right-2",
      disabled: !isCustomer,
      helpfulCount: helpfulCount,
      onClickHelpful: handleClickHelpful,
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.loginRequired"
      }),
      userMarkedHelpful: localMarkedHelpful
    }), /*#__PURE__*/_jsx(ReportButton, {
      className: "m-left-2",
      isCustomer: isCustomer,
      onClick: onReviewModalOpen
    })]
  }), [isCustomer, helpfulCount, handleClickHelpful, localMarkedHelpful]);
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    isCustomer: isCustomer,
    onReviewModalOpen: onReviewModalOpen,
    onReplyModalOpen: onReplyModalOpen,
    reviewModalOpen: reviewModalOpen,
    replyModalOpen: replyModalOpen,
    reportReviewModal: reportReviewModal,
    reportReplyModal: reportReplyModal,
    reviewActionsList: reviewsActionsList
  }));
};
export default withReportModalState;